// BUILD-049 (supporting model): the engine behind the My Work "HR tasks" section. Framework-free so it is tested without a DOM; useHrAssignedTasks.ts wraps it.
//
//   load()      : request -> fetch -> apply. Tagged (epoch, seq) at request time; an answer that a later request or a Caller change has overtaken changes nothing.
//   loadMore()  : the next page by the server's `nextOffset`; rows already shown are never repeated.
//   refresh()   : silent re-read of the current view; the rows stay on screen, a failure keeps them and leaves a note.
//   setView()   : open <-> done, then load().
//   act()       : start / note / Done on ONE row. The server's answer replaces that row; a refusal (no longer yours, closed, document-locked) says why and re-reads.
//   callerChanged(): clears everything the previous Caller had and loads again; an answer still in flight for the previous Caller is discarded.
//
// A fetch that throws is a FAILURE (the section says it cannot be shown), never "no tasks". Nothing here touches the other My Work lists.
// Erasable TypeScript; runtime imports are the model only.

import { actionErrorText, appendPage, initialHrTasksState, mergeRow, UNAVAILABLE_MESSAGE } from './hrAssignedTasksModel';
import type { HrTaskRow, HrTasksPage, HrTasksState, HrTasksView } from './hrAssignedTasksModel';

export interface HrTasksDeps {
  fetchPage: (params: { state: HrTasksView; limit: number; offset: number }) => Promise<HrTasksPage>;
  progress: (assignmentTaskId: string, input: { status?: 'in_progress' | 'completed'; note?: string | null }) => Promise<{ task: HrTaskRow }>;
  getEpoch: () => number;
  pageSize?: number;
}

export interface HrTasksController {
  getState: () => HrTasksState;
  subscribe: (listener: () => void) => () => void;
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  setView: (view: HrTasksView) => Promise<void>;
  act: (assignmentTaskId: string, input: { status?: 'in_progress' | 'completed'; note?: string | null }) => Promise<boolean>;
  callerChanged: () => Promise<void>;
  dismissRowError: (assignmentTaskId: string) => void;
}

export function createHrTasksController(deps: HrTasksDeps): HrTasksController {
  const pageSize = deps.pageSize ?? 25;
  let state: HrTasksState = initialHrTasksState();
  const listeners = new Set<() => void>();
  let seq = 0;

  const set = (next: HrTasksState): void => {
    state = next;
    for (const l of [...listeners]) l();
  };
  const stale = (tagEpoch: number, tagSeq: number): boolean => tagEpoch !== deps.getEpoch() || tagSeq !== seq;
  const validPage = (p: unknown): p is HrTasksPage => !!p && typeof p === 'object' && Array.isArray((p as HrTasksPage).tasks) && !!(p as HrTasksPage).page && typeof (p as HrTasksPage).page.total === 'number';

  async function read(mode: 'load' | 'refresh'): Promise<void> {
    const tagEpoch = deps.getEpoch();
    const tagSeq = ++seq;
    if (mode === 'load') set({ ...state, phase: 'loading', error: null, refreshNote: null });
    try {
      const page = await deps.fetchPage({ state: state.view, limit: pageSize, offset: 0 });
      if (stale(tagEpoch, tagSeq)) return;
      if (!validPage(page)) throw new Error('not a task list');
      set({
        ...state, phase: 'ready', tasks: page.tasks, total: page.page.total, nextOffset: typeof page.page.nextOffset === 'number' ? page.page.nextOffset : null,
        error: null, refreshNote: null, rowError: mode === 'load' ? {} : state.rowError, everShown: state.everShown || page.tasks.length > 0,
      });
    } catch {
      if (stale(tagEpoch, tagSeq)) return;
      // A failed first read is an error; a failed silent re-read keeps the rows and leaves a note.
      if (mode === 'refresh' && state.tasks.length > 0) set({ ...state, refreshNote: UNAVAILABLE_MESSAGE });
      else set({ ...state, phase: 'error', error: UNAVAILABLE_MESSAGE });
    }
  }

  return {
    getState: () => state,
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    load: () => read('load'),
    refresh: () => (state.phase === 'ready' || state.phase === 'error' ? read('refresh') : read('load')),
    async loadMore() {
      if (state.nextOffset === null || state.phase !== 'ready') return;
      const tagEpoch = deps.getEpoch();
      const tagSeq = ++seq;
      const offset = state.nextOffset;
      try {
        const page = await deps.fetchPage({ state: state.view, limit: pageSize, offset });
        if (stale(tagEpoch, tagSeq)) return;
        if (!validPage(page)) throw new Error('not a task list');
        set({ ...state, tasks: appendPage(state.tasks, page.tasks), total: page.page.total, nextOffset: typeof page.page.nextOffset === 'number' ? page.page.nextOffset : null, refreshNote: null });
      } catch {
        if (stale(tagEpoch, tagSeq)) return;
        set({ ...state, refreshNote: "More tasks couldn't be loaded. The ones shown are unchanged." });
      }
    },
    async setView(view) {
      if (view === state.view) return;
      set({ ...state, view, tasks: [], total: 0, nextOffset: null, rowError: {} });
      await read('load');
    },
    async act(assignmentTaskId, input) {
      if (state.busy[assignmentTaskId]) return false;
      const tagEpoch = deps.getEpoch();
      set({ ...state, busy: { ...state.busy, [assignmentTaskId]: true }, rowError: withoutKey(state.rowError, assignmentTaskId) });
      try {
        const answer = await deps.progress(assignmentTaskId, input);
        if (tagEpoch !== deps.getEpoch()) return false;
        if (!answer || !answer.task || answer.task.assignmentTaskId !== assignmentTaskId) throw new Error('unexpected answer');
        set({ ...mergeRow(state, answer.task), busy: withoutKey(state.busy, assignmentTaskId) });
        return true;
      } catch (thrown) {
        if (tagEpoch !== deps.getEpoch()) return false;
        const e = actionErrorText(thrown);
        set({ ...state, busy: withoutKey(state.busy, assignmentTaskId), rowError: { ...state.rowError, [assignmentTaskId]: e.text } });
        if (e.refresh) {
          await read('refresh');
          // The row may be gone after the re-read (no longer assigned to this person): the sentence then stands on the section instead of on a row that is not there.
          if (!state.tasks.some((t) => t.assignmentTaskId === assignmentTaskId) && tagEpoch === deps.getEpoch()) {
            set({ ...state, refreshNote: e.text, rowError: withoutKey(state.rowError, assignmentTaskId) });
          }
        }
        return false;
      }
    },
    async callerChanged() {
      seq += 1;
      set({ ...initialHrTasksState(), phase: 'loading' });
      await read('load');
    },
    dismissRowError(assignmentTaskId) { set({ ...state, rowError: withoutKey(state.rowError, assignmentTaskId) }); },
  };
}

function withoutKey<T extends Record<string, unknown>>(source: T, key: string): T {
  const copy: Record<string, unknown> = { ...source };
  delete copy[key];
  return copy as T;
}
